<script setup>
// Shown when adding a catalog SESSION is blocked because it overlaps something
// already on the schedule — another session OR one of the user's custom events.
// Opened via useModal().open(DoubleBookModal, { newSession, conflict }). The copy
// (and the four session/event variants) lives in FragmentsDoubleBookMessage.
const props = defineProps({
    newSession: { type: Object, required: true },
    conflict: { type: Object, required: true },
});
const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

import { modal_button_outline_2026 } from '~~/shared/utilities/ui';
import { conflictKindFromId } from '~~/shared/utilities/schedule/double-book-copy';
// Explicit rather than auto-imported: this file is mounted directly in the
// component test harness, which doesn't run Nuxt's auto-import pass.
import { useMyEvents } from '~/composables/useMyEvents';
import { useMyEventOpener } from '~/composables/useMyEventOpener';

const newTitle = computed(() => props.newSession?.title || 'this session');
const conflictTitle = computed(() => props.conflict?.title || '');
const conflictId = computed(() => props.conflict?.unique_id || null);

// This used to close the modal and then navigate a tick later, so the tray
// wouldn't mount underneath it. That raced: the modal's teardown overlapped the
// drawer's setup, and once the drawer was dismissed the page was left inert —
// nothing on the schedule could be clicked (UNB). `favorited.vue` papered over
// the same race with a 250ms timeout.
//
// Instead the modal is CONCEALED while the drawer is up — no unmount, so nothing
// to race — and comes back when the drawer closes. This is what my-event.vue
// (the custom-event half of this very modal) and find-a-session.vue already do.
//
// A CUSTOM-EVENT conflict has no route and no tray — its destination is the My
// Event editor, so it can't use the drawer path at all. Two things it needs:
// the RAW event row (the server only hands back `{ unique_id, title }`, and the
// edit form is populated from the row), resolved out of the shared myEvents
// list by stripping the `event:` prefix; and a way to reach the agenda's
// openMyEvent, which this dialog cannot inject. Same conceal-and-return shape:
// hide while the editor is up, reveal in `finally` so an error can't strand the
// modal invisible.
const myEvents = useMyEvents();
const { openMyEventFromDialog } = useMyEventOpener();
const editorOpen = ref(false);

const openConflict = async (id) => {
    if (conflictKindFromId(id) !== 'event') {
        await navigateTo(`/sessions/${id}`);
        return;
    }
    const eventId = id.slice('event:'.length);
    const raw = (myEvents.events.value || []).find((e) => String(e.id) === eventId);
    // No row means the list is stale (added on another tab); reload once rather
    // than dead-clicking.
    const target = raw || (await myEvents.loadEvents(), (myEvents.events.value || []).find((e) => String(e.id) === eventId));
    if (!target) return;

    editorOpen.value = true;
    try { await openMyEventFromDialog(target); }
    catch { /* the editor reports its own failures; just don't strand us hidden */ }
    finally { editorOpen.value = false; }
};

// The global modal middleware rewrites /sessions/{id} to `?drawer=`, which
// app.vue renders as the session drawer on top of this modal.
// Conceal only when the drawer becomes something OTHER than whatever was already
// showing when this modal opened.
//
// It used to be a bare `!!route.query.drawer`, which reads "a drawer is up, so
// hide". That is right when the attendee clicks through from here to a session —
// but wrong when the modal is opened FROM a page that already has a drawer open
// (any `?drawer=` URL, which is how the session tray deep-links). Then it was
// concealed from birth and never appeared at all: the progress screen flashed and
// the attendee was left with nothing (Jack, 2026-08-20).
const route = useRoute();
const drawerAtOpen = route.query.drawer ?? null;
const drawerOpen = computed(() => {
    const now = route.query.drawer ?? null;
    return !!now && now !== drawerAtOpen;
});
const concealed = computed(() => drawerOpen.value || editorOpen.value);
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        headline="Oops, looks like you’re trying to double book!"
        :concealed="concealed"
        @close="emit('close')"
    >
        <!-- White info box: alert icon + explanation naming both items -->
        <div class="flex items-start gap-[12px] w-full text-left bg-white rounded-[10px] p-[20px]">
            <svg class="shrink-0 mt-[3.5px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="8" fill="currentColor" />
                <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                <circle cx="8" cy="11.6" r="0.9" fill="white" />
            </svg>
            <FragmentsDoubleBookMessage
                new-kind="session"
                :new-title="newTitle"
                :conflict-title="conflictTitle"
                :conflict-id="conflictId"
                link-events
                @navigate="openConflict"
            />
        </div>

        <template #actions>
            <button type="button" :class="modal_button_outline_2026" @click="open = false">Close</button>
        </template>
    </FragmentsModalShell>
</template>
